/**
 * Width-aware sankey layout. Pure so it is testable in node.
 *
 * ECharts' defaults (5% left / 20% right margins, 20px nodes, labels to
 * the right of every node) work at desktop widths but at 320px put four
 * columns ~70px apart: label chips cover the neighbouring column and the
 * last column's labels clip off the right edge. This derives the
 * geometry from the chart width instead.
 */

import { NARROW } from "./chart-layout";

export interface SankeyGraph {
  nodes: ReadonlyArray<{ name: string }>;
  links: ReadonlyArray<{ source: string; target: string }>;
}

/** Column index per node (longest path from a source). Cycle-safe. */
export function sankeyDepths(graph: SankeyGraph): Map<string, number> {
  const depth = new Map<string, number>(graph.nodes.map((n) => [n.name, 0]));
  // Relax at most |nodes| times: enough for any DAG, and a bound on
  // graphs that still carry a cycle (`onCycle: "warn"`).
  for (let pass = 0; pass < graph.nodes.length; pass++) {
    let changed = false;
    for (const l of graph.links) {
      if (l.source === l.target) continue;
      const next = (depth.get(l.source) ?? 0) + 1;
      if (next > (depth.get(l.target) ?? 0)) {
        depth.set(l.target, next);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return depth;
}

export interface SankeyLayout {
  orient: "horizontal" | "vertical";
  left?: number;
  right?: number;
  nodeWidth?: number;
  nodeGap: number;
  label: Record<string, unknown>;
  /** Names of nodes with no outgoing link (labelled on their left). */
  sinks: ReadonlySet<string>;
  /** Number of columns. */
  columns: number;
}

/** Below this many px between columns the diagram turns vertical. */
export const SANKEY_MIN_COLUMN_PX = 72;

export function sankeyLayout(
  graph: SankeyGraph,
  ctx: { width: number; print: boolean; authorHeight?: number },
): SankeyLayout {
  const depths = sankeyDepths(graph);
  const columns = Math.max(1, ...depths.values()) + 1;
  const sinks = new Set(graph.nodes.map((n) => n.name));
  for (const l of graph.links) if (l.source !== l.target) sinks.delete(l.source);
  if (ctx.width <= 0) {
    return { orient: "horizontal", nodeGap: 12, label: {}, sinks: new Set(), columns };
  }
  const narrow = ctx.width < NARROW;
  const colW = (ctx.width - 16) / Math.max(1, columns - 1);
  if (!ctx.print && ctx.authorHeight === undefined && columns > 2 && colW < SANKEY_MIN_COLUMN_PX) {
    return {
      orient: "vertical",
      left: 8,
      right: 8,
      nodeWidth: 10,
      nodeGap: 8,
      label: { position: "bottom", width: Math.max(40, Math.floor(ctx.width / 3)), overflow: "truncate" },
      sinks: new Set(),
      columns,
    };
  }
  return {
    orient: "horizontal",
    left: 8,
    right: 8,
    nodeWidth: narrow ? 10 : 20,
    nodeGap: narrow ? 8 : 12,
    label: {
      width: Math.min(140, Math.max(48, Math.floor(colW * 0.45))),
      overflow: "truncate",
    },
    sinks,
    columns,
  };
}
